import { cn } from '../../utilities/cn';

export type SkeletonVariant = 'rect' | 'circle' | 'text';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: SkeletonVariant;
}

const variantClasses: Record<SkeletonVariant, string> = {
  rect: 'rounded-md',
  circle: 'rounded-full',
  text: 'h-[1em] rounded',
};

/**
 * Purely presentational loading placeholder. Renders a pulsing block tinted with
 * the semantic foreground colour so it works on every surface inside the light
 * shell. Size it with utility classes (e.g. `className="h-4 w-24"`).
 *
 * `motion-reduce:animate-none` honours prefers-reduced-motion. `aria-hidden` keeps
 * the placeholder out of the accessibility tree — wrap the loading region in a
 * container with `aria-busy` instead.
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'rect',
  className,
  ...otherProps
}) => (
  <div
    aria-hidden="true"
    className={cn(
      'animate-pulse bg-foreground/8 motion-reduce:animate-none',
      variantClasses[variant],
      className,
    )}
    {...otherProps}
  />
);

export interface SkeletonTextProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of placeholder lines to render. */
  lines?: number;
  /** Class applied to the final line so it reads as a shorter trailing line. */
  lastLineClassName?: string;
}

/**
 * A stack of {@link Skeleton} text lines, with a shortened last line for realism.
 */
export const SkeletonText: React.FC<SkeletonTextProps> = ({
  lines = 3,
  lastLineClassName = 'w-3/5',
  className,
  ...otherProps
}) => (
  <div className={cn('flex flex-col gap-2', className)} {...otherProps}>
    {Array.from({ length: lines }).map((_, index) => (
      <Skeleton
        key={index}
        variant="text"
        className={index === lines - 1 && lines > 1 ? lastLineClassName : undefined}
      />
    ))}
  </div>
);
